import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CompareDelta } from "@/components/clicks/CompareDelta";
import {
  formatGscCount,
  formatGscCtr,
  formatGscPosition,
} from "@/lib/gsc-display";
import { cn } from "@/lib/utils";
import type { GscKpiSummary } from "@/types/gsc";

interface SummaryCardsProps {
  data: GscKpiSummary;
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
}

type GscKpiMetricKey = "clicks" | "impressions" | "ctr" | "avg_position";

interface GscKpiCardConfig {
  key: GscKpiMetricKey;
  prevKey: keyof Pick<
    GscKpiSummary,
    "prev_clicks" | "prev_impressions" | "prev_ctr" | "prev_avg_position"
  >;
  label: string;
  format: (value: number) => string;
  invertDelta?: boolean;
}

const KPI_CARDS: GscKpiCardConfig[] = [
  {
    key: "clicks",
    prevKey: "prev_clicks",
    label: "Total Clicks",
    format: formatGscCount,
  },
  {
    key: "impressions",
    prevKey: "prev_impressions",
    label: "Total Impressions",
    format: formatGscCount,
  },
  {
    key: "ctr",
    prevKey: "prev_ctr",
    label: "Avg CTR",
    format: formatGscCtr,
  },
  {
    key: "avg_position",
    prevKey: "prev_avg_position",
    label: "Avg Position",
    format: formatGscPosition,
    invertDelta: true,
  },
];

const SummaryCardSkeleton = () => (
  <Card className="border-border shadow-none">
    <CardHeader className="space-y-0 pb-2">
      <Skeleton className="h-3 w-32" />
    </CardHeader>
    <CardContent className="space-y-2">
      <Skeleton className="h-9 w-36" />
      <Skeleton className="h-4 w-28" />
    </CardContent>
  </Card>
);

export const SummaryCards = ({
  data,
  isLoading = false,
  isFetching = false,
  className,
}: SummaryCardsProps) => {
  if (isLoading) {
    return (
      <div
        className={cn("grid gap-4 sm:grid-cols-2 xl:grid-cols-4", className)}
        aria-busy
        aria-label="Loading GSC summary"
      >
        {KPI_CARDS.map((card) => (
          <SummaryCardSkeleton key={card.key} />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid gap-4 sm:grid-cols-2 xl:grid-cols-4",
        isFetching && "pointer-events-none opacity-60",
        className,
      )}
      aria-busy={isFetching}
      aria-label="GSC summary metrics"
    >
      {KPI_CARDS.map((card) => {
        const current = data[card.key] ?? 0;
        const previous = data[card.prevKey] ?? 0;

        return (
          <Card key={card.key} className="border-border shadow-none">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
                {card.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <div className="text-3xl font-bold tracking-tight text-foreground tabular-nums sm:text-4xl">
                {card.format(current)}
              </div>
              <CompareDelta
                current={current}
                previous={previous}
                invertPolarity={card.invertDelta}
                periodLabel="vs prev period"
                className="justify-start"
              />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
